/**
 * Reusable button component.
 *
 * Combines rendering from RoundRect and hover/press behavior from
 * ButtonBehavior.
 *
 * TODO(kevinb):
 * - figure out a way to allow a different width on mobile
 */
import * as React from "react";

import * as tokens from "@khanacademy/wonder-blocks-tokens";

import ButtonBase from "./button-base.tsx";
import {outlineStyle, solidStyle, textStyle} from "./button-utils.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedHoverBehavior from "./deprecated-hover-behavior.ts";
import type {ButtonCorners, ButtonSize, Colors} from "./types.ts";

type Props = JSX.LibraryManagedAttributes<
    typeof ButtonBase,
    React.ComponentProps<typeof ButtonBase>
> & {
    type?: "primary" | "secondary" | "text";
    size?: ButtonSize;
    corners?: ButtonCorners;
    disabledStateIsOpaque?: boolean;
    // Whether the link should appear in a 'highlighted' state. In
    // practice, this will apply the same styles that are applied on-hover.
    highlighted?: boolean;
    color?: string;
    colors?: Colors;
    hoverColor?: string;
    hoverColors?: Colors;
    onClick?: (e: React.MouseEvent) => unknown;
};

/**
 * @deprecated Please use Wonder Blocks Button instead.
 */
export default class DeprecatedButton extends React.Component<Props> {
    buttonBase: ButtonBase | null | undefined;

    focus() {
        this.buttonBase?.focus();
    }

    getBoundingClientRect(): ClientRect | null | undefined {
        return this.buttonBase?.getBoundingClientRect();
    }

    getStyle():
        | ((color: string) => {
              backgroundColor: string;
              borderColor: string;
              textColor: string;
          })
        | ((
              color: string,
              backgroundColor?: string,
          ) => {
              backgroundColor: string;
              borderColor: string;
              textColor: string;
          }) {
        const {type} = this.props;

        if (type === "primary") {
            return solidStyle;
        }

        if (type === "secondary") {
            return outlineStyle;
        }

        if (type === "text") {
            return textStyle;
        }

        return solidStyle;
    }

    render(): React.ReactNode {
        const {
            color,
            colors,
            hoverColor,
            hoverColors,
            highlighted,
            disabled,
            disabledStateIsOpaque,
            href,
            size,
            type,
            onClick,
            ...restProps
        } = this.props;

        return (
            <DeprecatedHoverBehavior disabled={disabled} onClick={onClick}>
                {(state, handlers) => {
                    // @ts-expect-error - TS2554 - Expected 0 arguments, but got 1.
                    const style = this.getStyle(type);
                    let opacity: undefined | number = undefined;
                    let normalColorProps: Record<string, any> = {};
                    let colorProps: Record<string, any> = {};

                    if (colors) {
                        normalColorProps = colors;
                    } else if (color) {
                        normalColorProps = style(color);
                    } else {
                        normalColorProps = style(tokens.color.green);
                    }

                    if (disabled) {
                        if (disabledStateIsOpaque) {
                            colorProps = normalColorProps;
                            opacity = 0.5;
                        } else {
                            colorProps = style(tokens.color.offBlack32);
                        }
                    } else if (state.hovered || state.focused || highlighted) {
                        if (hoverColors) {
                            colorProps = hoverColors;
                        } else if (hoverColor) {
                            colorProps = style(hoverColor);
                        } else if (colors) {
                            colorProps = colors;
                        } else if (color) {
                            colorProps = style(color);
                        } else {
                            colorProps = style(tokens.color.teal);
                        }
                    } else {
                        colorProps = normalColorProps;
                    }

                    return (
                        <ButtonBase
                            {...colorProps}
                            {...handlers}
                            {...restProps}
                            disabled={disabled}
                            opacity={opacity}
                            href={href}
                            size={size || "default"}
                            tag={href ? "a" : "button"}
                            ref={(node) => (this.buttonBase = node)}
                        />
                    );
                }}
            </DeprecatedHoverBehavior>
        );
    }
}
